import React, { useEffect, useState } from "react";
import { Monitor, X } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────
// MobileDesktopModal
// Shows a one-time-per-session "Best on desktop" notice when a logged-in
// user lands inside the platform on a viewport ≤ 768px.
//
// • Mounted only inside <ProtectedRoute>, so it never appears on the
//   marketing site or any public/auth page.
// • Dismissed via "Got it" or the ✕ — flag stored in sessionStorage so
//   it re-appears on next login (tab close clears sessionStorage).
// • Auto-hides if the user rotates to a wider landscape view.
// ─────────────────────────────────────────────────────────────────────
const STORAGE_KEY = "voxinity_mobile_modal_dismissed";

const MobileDesktopModal = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const evaluate = () => {
      const isMobile = window.matchMedia("(max-width: 768px)").matches;
      const dismissed = sessionStorage.getItem(STORAGE_KEY) === "1";
      setOpen(isMobile && !dismissed);
    };

    evaluate();

    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => evaluate();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const dismiss = () => {
    sessionStorage.setItem(STORAGE_KEY, "1");
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center px-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mobile-desktop-modal-title"
      data-testid="mobile-desktop-modal"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={dismiss}
      />

      {/* Card */}
      <div className="relative w-full max-w-[360px] rounded-2xl bg-[#0a0b0f] border border-white/10 shadow-2xl shadow-black/50 overflow-hidden animate-[modalIn_0.25s_ease-out]">
        {/* Subtle gradient accent at the top */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-teal-400 to-transparent opacity-80" />

        {/* Close button */}
        <button
          onClick={dismiss}
          aria-label="Close"
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/[0.06] transition-colors"
          data-testid="mobile-desktop-modal-close"
        >
          <X size={16} />
        </button>

        <div className="px-7 pt-8 pb-7 text-center">
          {/* Icon tile */}
          <div className="mx-auto w-14 h-14 rounded-2xl bg-teal-400/10 border border-teal-400/25 flex items-center justify-center mb-5 shadow-[0_0_30px_-5px_rgba(45,212,191,0.4)]">
            <Monitor size={26} className="text-teal-300" strokeWidth={1.7} />
          </div>

          <h2
            id="mobile-desktop-modal-title"
            className="font-display font-semibold text-2xl text-white tracking-tight mb-3"
          >
            Best on Desktop
          </h2>

          <p className="text-[14px] text-gray-400 leading-relaxed mb-7">
            We've optimized Voxinity for desktop. For the smoothest
            experience managing your AI agents, please log in from your
            computer.
          </p>

          <button
            onClick={dismiss}
            className="w-full py-3 rounded-full bg-gradient-to-r from-teal-400 to-teal-500 text-space-900 font-semibold text-[15px] hover:shadow-xl hover:shadow-teal-400/30 transition-all duration-200"
            data-testid="mobile-desktop-modal-got-it"
          >
            Got it
          </button>
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.96) translateY(6px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default MobileDesktopModal;
